import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AlertDialogTone } from '@/components/AlertDialog';

/**
 * Global alert-dialog state — a single app-level `<GlobalAlertDialog>` reads this so any
 * code (components, and imperative call sites that previously used `window.alert`) can raise
 * a blocking, must-acknowledge modal via `dispatch(showAlertDialog(...))` / the `useAlert` hook.
 * Mirrors the snackbar slice pattern.
 */
export interface AlertDialogState {
  open: boolean;
  message: string;
  title?: string;
  tone: AlertDialogTone;
  confirmLabel?: string;
}

export interface ShowAlertDialogPayload {
  message: string;
  title?: string;
  tone?: AlertDialogTone;
  confirmLabel?: string;
}

const initialState: AlertDialogState = {
  open: false,
  message: '',
  tone: 'error',
};

const alertDialogSlice = createSlice({
  name: 'alertDialog',
  initialState,
  reducers: {
    showAlertDialog(state, action: PayloadAction<ShowAlertDialogPayload | string>) {
      const payload = typeof action.payload === 'string' ? { message: action.payload } : action.payload;
      state.open = true;
      state.message = payload.message;
      state.title = payload.title;
      state.tone = payload.tone ?? 'error';
      state.confirmLabel = payload.confirmLabel;
    },
    // Keep message/title on close (open=false) so the dialog fades without a flash of empty content.
    closeAlertDialog(state) {
      state.open = false;
    },
  },
});

export const { showAlertDialog, closeAlertDialog } = alertDialogSlice.actions;
export default alertDialogSlice.reducer;
